import { Api } from '@/Api';
import { useRoute } from 'vue-router';
import { DEBUG_NAMESPACE, useDebug } from '@/composables/useDebug';
import { TrackEvents, TrackClickEvents, useTrack } from '@/composables/useTrack';
import { ItemSharingMode, useViewerItemStore } from '@/modules/viewer/store/useViewerItemStore';

const { debug } = useDebug(DEBUG_NAMESPACE.TRACKING);

// Re-export for convenience
export { TrackEvents, TrackClickEvents };

// Surfaces this composable emits share completions from.
export type ViewerShareSurface = 'share_modal' | 'viewer_toolbar' | 'viewer_actions' | 'analytics_share';

export interface ItemShareCompletedProperties {
  shareChannel: 'copy_link' | 'email';
  // 'dashboard' is emitted only by the legacy dashboard JS (app/assets/javascripts/dashboard/index.js), never this composable.
  shareSurface: ViewerShareSurface | 'dashboard';
  shareSetting: ItemSharingMode | null;
  recipientCount?: number;
}

export const useViewerTrack = () => {
  const route = useRoute();
  const itemStore = useViewerItemStore();
  const { track, trackClick, identify } = useTrack();

  // Frontend properties remain camelCase; Api.track.create serializes the request as snake_case for Amplitude.
  const trackWithItemMeta = (event: TrackEvents, data: object = {}, asBeacon?: false) => {
    const trackData = {
      params: route?.params,
      route: route?.name,
      design: 'copernicus',
      item_type: itemStore.type,
      hashid: itemStore.id,
      source_app: 'web',
      ...data,
    };

    debug(`[trackItemOwner] - ${event}`, trackData);
    return Api.track.create(event, trackData, asBeacon);
  };

  // Public generic tracker. ITEM_SHARE_COMPLETED is intentionally excluded so its identity
  // stamping (below) can't be bypassed — fire that event via trackItemShareCompleted.
  const trackItemOwner = (event: Exclude<TrackEvents, TrackEvents.ITEM_SHARE_COMPLETED>, data: object = {}, asBeacon?: false) => {
    return trackWithItemMeta(event, data, asBeacon);
  };

  // Stamp the shared item's identity (item/owner/org) so completions can be measured
  // against created items. owner/org are the item owner's, resolved once here rather than
  // per call site. recipientCount is always present (0 for channels without recipients).
  const trackItemShareCompleted = (data: ItemShareCompletedProperties) => {
    return trackWithItemMeta(TrackEvents.ITEM_SHARE_COMPLETED, {
      ...data,
      recipientCount: data.recipientCount ?? 0,
      itemId: itemStore.id,
      ownerId: itemStore.userId,
      orgId: itemStore.itemOrganization?.id,
    });
  };

  return { track, trackItemOwner, trackItemShareCompleted, trackClick, identify };
};
